import classNames from 'classnames';
import React from 'react';

import styles from './plan-card.module.less';

/** A single row in the card's feature list. */
export interface PlanCardFeature {
    /** Leading icon, usually a tick. */
    icon: React.ReactNode;
    /** Feature copy. Accepts a node so loading skeletons can be rendered. */
    text: React.ReactNode;
}

/** Props for the PlanCard component. */
export interface PlanCardProps {
    /** Card title, e.g. "Personal Plan". */
    title: React.ReactNode;
    /** Short supporting line under the title. */
    subtitle?: React.ReactNode;
    /** Value propositions listed in the body of the card. */
    features: PlanCardFeature[];
    /** Price shown in the shaded footer area. */
    priceComponent?: React.ReactNode;
    /** Billing details rendered next to the price, e.g. "Cancel anytime.". */
    priceDetails?: React.ReactNode;
    /** Renders the billing details on their own line under the price instead of beside it. */
    stackPriceDetails?: boolean;
    /** Optional badge rendered at the top of the price area, e.g. a discount ribbon. */
    priceBadge?: React.ReactNode;
    /** Optional call to action rendered below the price. */
    cta?: React.ReactNode;
    /** Highlights the card with the brand purple treatment. */
    highlighted?: boolean;
    /**
     * Shows a floating badge above the card, matching `CustomPlanCard`'s
     * `isRecommended` treatment.
     */
    isRecommended?: boolean;
    /** Label for the recommended badge. Defaults to "Best value". */
    recommendedLabel?: React.ReactNode;
}

/**
 * ### PlanCard
 *
 * Presentational plan card that matches the Starter Plan landing page card
 * (`CustomPlanCard` from `@udemy/react-plan-comparison`).
 *
 * We keep a local copy instead of using the shared component because this page needs
 * a card without a CTA (the marketplace option) and custom billing copy next to the
 * price, both of which the shared component hard-codes.
 */
export const PlanCard = ({
    title,
    subtitle,
    features,
    priceComponent,
    priceDetails,
    stackPriceDetails,
    priceBadge,
    cta,
    highlighted,
    isRecommended,
    recommendedLabel,
}: PlanCardProps) => {
    return (
        <div
            className={classNames(styles['card'], {
                [styles['highlighted']]: highlighted,
                [styles['recommended']]: isRecommended,
            })}
        >
            {isRecommended && (
                <div
                    className={classNames('ud-heading-sm', styles['recommended-badge'])}
                    data-purpose="recommended-badge"
                >
                    {recommendedLabel}
                </div>
            )}
            <div className={styles['card-content']}>
                <div className={styles['card-header']}>
                    <h3
                        className={classNames('ud-heading-xl', styles['title'])}
                        data-purpose="title"
                    >
                        {title}
                    </h3>
                    {subtitle && (
                        <p
                            className={classNames('ud-text-md', styles['subtitle'])}
                            data-purpose="subtitle"
                        >
                            {subtitle}
                        </p>
                    )}
                </div>
                <div className={styles['card-body']}>
                    <ul className={styles['features-list']}>
                        {features.map((feature, index) => (
                            <li
                                key={index}
                                className={classNames('ud-text-md', styles['feature-item'])}
                            >
                                <span className={styles['feature-icon']}>{feature.icon}</span>
                                <span className={styles['feature-text']}>{feature.text}</span>
                            </li>
                        ))}
                    </ul>
                    <div className={styles['price-section']}>
                        {priceBadge}
                        <div
                            className={classNames(styles['price-row'], {
                                [styles['price-row-stacked']]: stackPriceDetails,
                            })}
                        >
                            <div
                                className={classNames('ud-heading-md', styles['price'])}
                                data-purpose="price"
                            >
                                {priceComponent}
                            </div>
                            {priceDetails && (
                                <div
                                    className={classNames('ud-text-sm', styles['price-details'])}
                                    data-purpose="price-details"
                                >
                                    {priceDetails}
                                </div>
                            )}
                        </div>
                        {cta && <div className={styles['cta']}>{cta}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};
